import { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Plus, Star } from 'lucide-react';

interface ListingImageUploaderProps {
  existingImages: string[];
  onImagesChange: (files: File[], existingKept: string[]) => void;
}

export function ListingImageUploader({ existingImages, onImagesChange }: ListingImageUploaderProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [keptImages, setKeptImages] = useState<string[]>(existingImages);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sincronizar con props externas si cambian (ej: carga inicial de edición)
  useEffect(() => {
    setKeptImages(existingImages);
  }, [existingImages]);

  // Notificar al padre cada vez que hay cambios
  useEffect(() => {
    onImagesChange(selectedFiles, keptImages);
  }, [selectedFiles, keptImages]);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    
    const validFiles: File[] = [];
    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024) { // Límite inicial 10MB (se comprimirá)
        validFiles.push(file);
      } else {
        alert(`El archivo ${file.name} no es válido (debe ser imagen < 10MB)`);
      }
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeSelected = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExisting = (index: number) => {
    setKeptImages(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {/* Área de Drop / Input */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all ${
          dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <div className="bg-blue-100 p-3 rounded-full mb-3 text-blue-600">
            <Upload className="w-6 h-6" />
        </div>
        <p className="text-sm font-bold text-slate-700">
          Haz clic o arrastra tus fotos aquí
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Soporta JPG, PNG, WEBP
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {/* Grid de Previsualización */}
      {(keptImages.length > 0 || selectedFiles.length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {/* Imágenes Existentes (Edición) */}
          {keptImages.map((url, idx) => (
            <div key={`existing-${idx}`} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200 group bg-slate-100">
              <img src={url} alt="Preview" className="w-full h-full object-cover" />
              
              {/* Badge Portada (solo para el primero absoluto) */}
              {idx === 0 && (
                <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm flex items-center gap-1">
                    <Star className="w-3 h-3 fill-yellow-900" /> Portada
                </div>
              )}

              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => removeExisting(idx)}
                  className="bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Nuevas Imágenes Seleccionadas */}
          {selectedFiles.map((file, idx) => {
            const isCover = keptImages.length === 0 && idx === 0;
            return (
                <div key={`new-${idx}`} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-blue-200 group bg-slate-100">
                <img 
                    src={URL.createObjectURL(file)} 
                    alt="Preview" 
                    className="w-full h-full object-cover" 
                    onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
                />
                
                {isCover && (
                    <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm flex items-center gap-1">
                        <Star className="w-3 h-3 fill-yellow-900" /> Portada
                    </div>
                )}

                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                    type="button"
                    onClick={() => removeSelected(idx)}
                    className="bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                    >
                    <X className="w-4 h-4" />
                    </button>
                </div>
                <span className="absolute bottom-1 right-1 bg-blue-600 text-white text-[10px] px-1.5 rounded">Nueva</span>
                </div>
            );
          })}
          
          {/* Botón mini para añadir más */}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="aspect-[4/3] rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 hover:text-blue-500 hover:border-blue-300 hover:bg-slate-50 transition-colors"
          >
            <Plus className="w-6 h-6" />
            <span className="text-xs font-medium mt-1">Añadir</span>
          </button>
        </div>
      )}
    </div>
  );
}
